// Parsing for detected-issue citation references. Kept free of heavy UI imports
// (e.g. the range-view chart stack) so it can be used by routing logic and unit
// tests without pulling in those modules.

// FHIR resource types whose citations can open one of the member-detail data
// view modals. References pointing at any other type (e.g. literature DOIs or
// resources without a dedicated modal) are rendered as plain text instead.
export const CITATION_RESOURCE_TYPES = [
  'MedicationRequest',
  'MedicationStatement',
  'Condition',
  'AllergyIntolerance',
  'Procedure',
  'Immunization',
] as const;

export type CitationResourceType = (typeof CITATION_RESOURCE_TYPES)[number];

/** A citation that opens a member-detail FHIR data-view modal via `getById`. */
export type ParsedFhirCitation = {
  kind: 'fhir';
  resourceType: CitationResourceType;
  id: string;
};

/**
 * A lab/biomarker citation encoded as `system|code|resourceId`. These open the
 * shared range view (the same "medical result" handling clinician chat uses)
 * rather than a single FHIR resource fetch.
 */
export type ParsedMedicalResultCitation = {
  kind: 'medicalResult';
  system?: string;
  code?: string;
  resourceId: string;
};

export type ParsedCitationReference =
  | ParsedFhirCitation
  | ParsedMedicalResultCitation;

/**
 * Parse a detected-issue citation reference into something openable in a modal:
 *
 * - `system|code|resourceId` (observations/labs) -> a medical-result citation
 *   that opens the range view.
 * - `MedicationRequest/mr2`, `Condition/c1`, etc. -> a FHIR citation that opens
 *   the corresponding data-view modal.
 *
 * Returns `null` for unsupported types and non-FHIR references such as
 * `doi:10.1000/xyz` or a bare `Observation/<id>` (no coding to drive the range
 * view), which are rendered as plain text instead.
 */
export const parseCitationReference = (
  reference: string | undefined,
): ParsedCitationReference | null => {
  if (!reference) {
    return null;
  }
  const trimmed = reference.trim();
  if (trimmed === '') {
    return null;
  }
  // Observation/lab "medical result" encoding: system|code|resourceId.
  if (trimmed.includes('|')) {
    const [system, code, resourceId] = trimmed.split('|');
    if (!resourceId) {
      return null;
    }
    return { kind: 'medicalResult', system, code, resourceId };
  }
  const match = /^([A-Za-z]+)\/([^/]+)$/.exec(trimmed);
  if (!match) {
    return null;
  }
  const [, resourceType, id] = match;
  if ((CITATION_RESOURCE_TYPES as readonly string[]).includes(resourceType)) {
    return {
      kind: 'fhir',
      resourceType: resourceType as CitationResourceType,
      id,
    };
  }
  return null;
};

// A UUID forum post id, optionally followed by a query/fragment suffix. The
// Zori Pulse conversation-review child cites the member<->Zori chat posts it
// reviewed by their raw forum post id, which is the same id the chat renders
// each message under (`<Message id={post.id}>`). The reference may also carry a
// trailing query string (e.g. `?trace_id=...`) and arrive URL-encoded
// (e.g. `%3Ftrace_id%3D...`). We decode, drop any `?`/`#` suffix, and keep the
// leading UUID so these can deep-link into the member chat and scroll/highlight
// the cited message rather than rendering as an opaque id.
const UUID_REFERENCE_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Parse a detected-issue citation reference that points at a member-chat
 * message. Returns the chat message (forum post) id when the reference is a bare
 * UUID (optionally URL-encoded and/or carrying a `?trace_id=...` style suffix),
 * or `null` otherwise (FHIR/medical-result references are handled by
 * `parseCitationReference`; everything else renders as plain text).
 */
export const parseChatMessageReference = (
  reference: string | undefined,
): string | null => {
  if (!reference) {
    return null;
  }
  const trimmed = reference.trim();
  let decoded = trimmed;
  try {
    decoded = decodeURIComponent(trimmed);
  } catch {
    // Malformed percent-encoding: fall back to the raw value.
    decoded = trimmed;
  }
  // Drop any query string / fragment (e.g. `?trace_id=...`) and keep the id.
  const id = decoded.split(/[?#]/, 1)[0].trim();
  return UUID_REFERENCE_REGEX.test(id) ? id : null;
};
